import { produce } from 'immer';
import { mapValues, omit, without } from 'lodash';
import { Query } from 'redux-little-router';

import {
    CHANGE_TARGETING_IN_PRICE_PACKAGE,
    CLEAR_ERRORS_GEO_IN_PRICE_PACKAGE,
    PricePackagesEditAction,
    SAVE_PRICE_PACKAGES,
    SHOW_COMMON_PRICE_PACKAGE_SAVE_ERRORS,
    SHOW_PRICE_PACKAGE_EDIT_ERRORS,
    ShowPricePackageEditErrorsAction,
    UPDATE_PRICE_PACKAGE_BEHAVIORS,
    UPDATE_PRICE_PACKAGE_CURRENCY,
    UPDATE_PRICE_PACKAGE_EDITOR_REGIONS,
    UPDATE_PRICE_PACKAGE_MAX_SHOWS,
    UPDATE_PRICE_PACKAGE_MIN_SHOWS,
    UPDATE_PRICE_PACKAGE_NAME,
    UPDATE_PRICE_PACKAGE_PRICE,
    UPDATE_PRICE_PACKAGE_TICKET,
    UPDATE_PRICE_PACKAGE_VIEW_TYPE,
    UPDATE_PRICE_PACKAGES_DATA_FOR_EDIT,
    UpdatePricePackageBehaviorsAction,
    UpdatePricePackageCurrencyAction,
    UpdatePricePackageEditorRegionsAction,
    UpdatePricePackageMaxShowsAction,
    UpdatePricePackageMinShowsAction,
    UpdatePricePackageNameAction,
    UpdatePricePackagePriceAction,
    UpdatePricePackageTicketAction,
    UpdatePricePackageViewTypeAction
} from '../actions/pricePackagesEdit';
import { BEHAVIORS_LTV } from '../constants';
import { RegionsHelpers } from '../service/RegionsHelpers/RegionsHelpers';
import { PageStatus } from '../typings/common';
import { GdViewType } from '../typings/graphql-grid';
import {
    PricePackageId,
    PricePackagesEditScreenUrlSearchParams,
    PricePackagesEditState
} from '../typings/pricePackages';

export interface PricePackagesEditPageUrlData {
    isNew: boolean;
    pricePackagesIds: PricePackageId[];
}

export const getPricePackagesEditPageData = (queryParams: Query): PricePackagesEditPageUrlData => {
    return {
        pricePackagesIds: queryParams[PricePackagesEditScreenUrlSearchParams.pricePackagesIds]
            ? queryParams[PricePackagesEditScreenUrlSearchParams.pricePackagesIds].split(',')
            : [],
        isNew: queryParams[PricePackagesEditScreenUrlSearchParams.isNew]
            ? Boolean(+queryParams[PricePackagesEditScreenUrlSearchParams.isNew])
            : false
    };
};

const updatePricePackageRegions = (
    state: PricePackagesEditState,
    action: UpdatePricePackageEditorRegionsAction
): PricePackagesEditState => {
    const { id, selectedMap, geoType, isFixed, tree } = action.data;
    const pricePackage = state.pricePackages[id];

    const regionIds = RegionsHelpers.convertRegionsSelectedInfoToFlattenList(
        RegionsHelpers.getSelectedRegionsInfo(tree, selectedMap)
    );

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [id]: {
                ...pricePackage,
                regions: {
                    ...pricePackage.regions,
                    selectedMap: selectedMap,
                    regionIds: regionIds,
                    type: geoType,
                    isFixed: isFixed
                }
            }
        }
    };
};

const updatePricePackageName = (
    state: PricePackagesEditState,
    action: UpdatePricePackageNameAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                title: action.name
            }
        }
    };
};

const updatePricePackageMinShows = (
    state: PricePackagesEditState,
    action: UpdatePricePackageMinShowsAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];
    const errors = omit(pricePackage.errors, ['orderVolumeMin', 'orderVolumeMax']);

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                orderVolumeMin: action.value,
                errors: { ...errors }
            }
        }
    };
};

const updatePricePackageMaxShows = (
    state: PricePackagesEditState,
    action: UpdatePricePackageMaxShowsAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];
    const errors = omit(pricePackage.errors, ['orderVolumeMin', 'orderVolumeMax']);

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                orderVolumeMax: action.value,
                errors: { ...errors }
            }
        }
    };
};

const updatePricePackagePrice = (
    state: PricePackagesEditState,
    action: UpdatePricePackagePriceAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];
    const errors = omit(pricePackage.errors, ['price']);

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                price: action.value,
                errors: { ...errors }
            }
        }
    };
};

const updatePricePackageCurrency = (
    state: PricePackagesEditState,
    action: UpdatePricePackageCurrencyAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];
    const errors = omit(pricePackage.errors, ['clientsCurrency', 'price']);

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                currency: action.currency,
                errors: { ...errors }
            }
        }
    };
};

const updatePricePackageViewType = (
    state: PricePackagesEditState,
    action: UpdatePricePackageViewTypeAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];
    const oldViewTypes = pricePackage.targetingsFixed.viewTypes || [];
    let newViewTypes: GdViewType[];

    if (action.checked) {
        newViewTypes = oldViewTypes.concat(action.viewType);
    } else {
        if (oldViewTypes.length === 1) {
            newViewTypes = oldViewTypes;
        } else {
            newViewTypes = without(oldViewTypes, action.viewType);
        }
    }

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                targetingsFixed: {
                    ...pricePackage.targetingsFixed,
                    viewTypes: newViewTypes
                }
            }
        }
    };
};

const updatePricePackageBehaviors = (
    state: PricePackagesEditState,
    action: UpdatePricePackageBehaviorsAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                targetingsCustom: {
                    ...pricePackage.targetingsCustom,
                    behaviors: action.checked ? [BEHAVIORS_LTV] : []
                }
            }
        }
    };
};

const updatePricePackageTicket = (
    state: PricePackagesEditState,
    action: UpdatePricePackageTicketAction
): PricePackagesEditState => {
    const pricePackage = state.pricePackages[action.pricePackageId];
    const errors = omit(pricePackage.errors, 'trackerUrl');

    return {
        ...state,
        pricePackages: {
            ...state.pricePackages,
            [action.pricePackageId]: {
                ...pricePackage,
                trackerUrl: action.value,
                errors: { ...errors }
            }
        }
    };
};

export const PricePackagesEditInitialState: PricePackagesEditState = {
    pageStatus: PageStatus.LOADING,
    errors: [],
    pricePackages: {},
    pricePackagesIds: [],
    isNew: false
};

const showPricePackageEditError = (
    state: PricePackagesEditState,
    action: ShowPricePackageEditErrorsAction
): PricePackagesEditState => {
    return {
        ...state,
        pageStatus: PageStatus.LOADED,
        pricePackages: mapValues(state.pricePackages, pricePackage => {
            return {
                ...pricePackage,
                errors: action.errors[pricePackage.id] ? action.errors[pricePackage.id] : pricePackage.errors
            };
        })
    };
};

export function pricePackagesEditReducer(
    state: PricePackagesEditState = PricePackagesEditInitialState,
    action: PricePackagesEditAction
): PricePackagesEditState {
    switch (action.type) {
        case UPDATE_PRICE_PACKAGES_DATA_FOR_EDIT:
            return {
                ...state,
                pricePackages: action.pricePackages,
                pricePackagesIds: Object.keys(action.pricePackages),
                pageStatus: PageStatus.LOADED
            };
        case SAVE_PRICE_PACKAGES:
            return {
                ...state,
                errors: [],
                pageStatus: PageStatus.SAVING
            };
        case SHOW_COMMON_PRICE_PACKAGE_SAVE_ERRORS:
            return {
                ...state,
                errors: action.errors,
                pageStatus: PageStatus.LOADED
            };
        case UPDATE_PRICE_PACKAGE_EDITOR_REGIONS:
            return updatePricePackageRegions(state, action);
        case UPDATE_PRICE_PACKAGE_NAME:
            return updatePricePackageName(state, action);
        case UPDATE_PRICE_PACKAGE_MIN_SHOWS:
            return updatePricePackageMinShows(state, action);
        case UPDATE_PRICE_PACKAGE_MAX_SHOWS:
            return updatePricePackageMaxShows(state, action);
        case UPDATE_PRICE_PACKAGE_PRICE:
            return updatePricePackagePrice(state, action);
        case UPDATE_PRICE_PACKAGE_CURRENCY:
            return updatePricePackageCurrency(state, action);
        case UPDATE_PRICE_PACKAGE_VIEW_TYPE:
            return updatePricePackageViewType(state, action);
        case UPDATE_PRICE_PACKAGE_BEHAVIORS:
            return updatePricePackageBehaviors(state, action);
        case UPDATE_PRICE_PACKAGE_TICKET:
            return updatePricePackageTicket(state, action);
        case SHOW_PRICE_PACKAGE_EDIT_ERRORS:
            return showPricePackageEditError(state, action);
        case CHANGE_TARGETING_IN_PRICE_PACKAGE:
            return produce(state, draft => {
                draft.pricePackages[action.id].regions.isFixed = action.isFixed;
            });
        case CLEAR_ERRORS_GEO_IN_PRICE_PACKAGE:
            return produce(state, draft => {
                draft.pricePackages[action.id].errors = omit(draft.pricePackages[action.id].errors, [
                    'targetingsFixed',
                    'targetingsCustom'
                ]);
            });
        default:
            return state;
    }
}
